import { useEffect, useState } from 'react';
import moment from 'moment';
import api from '../api';
import s from './PortalSidePanel.module.scss';

const PLAN_LABELS = {
    free: 'Бесплатный', trial: 'Пробный', basic: 'Базовый',
    extended: 'Расширенный', professional: 'Профессиональный', other: 'Другой',
};

const PLAN_OPTIONS = [
    { value: 'extended', label: 'Расширенный' },
    { value: 'professional', label: 'Профессиональный' },
    { value: 'trial', label: 'Пробный' },
    { value: 'other', label: 'Другой (custom)' },
];

// Лимиты по умолчанию из тарифных планов
const PLAN_DEFAULTS = {
    trial:        { analyses: 100,  extUsers: 1,    resumeUpdates: null },
    basic:        { analyses: 100,  extUsers: 1,    resumeUpdates: null },
    extended:     { analyses: 300,  extUsers: 5,    resumeUpdates: null },
    professional: { analyses: 900,  extUsers: null, resumeUpdates: null },
};

function planHint(plan, key) {
    const d = PLAN_DEFAULTS[plan];
    if (!d) return null;
    const val = d[key];
    return val == null ? 'Без ограничений' : String(val);
}

function PlanBadge({ plan }) {
    const cls = { free: s.bFree, trial: s.bTrial, basic: s.bBasic, extended: s.bExt, professional: s.bPro }[plan] || s.bOther;
    return <span className={`${s.badge} ${cls}`}>{PLAN_LABELS[plan] || plan}</span>;
}

function UsageBar({ used, available, label }) {
    const pct = available ? Math.min((used / available) * 100, 100) : 0;
    return (
        <div className={s.usageRow}>
            <div className={s.usageTop}>
                <span>{label}</span>
                <span className={s.usageNums}>{used} / {available ?? '∞'}</span>
            </div>
            {available != null && (
                <div className={s.track}><div className={`${s.fill} ${pct >= 90 ? s.fillWarn : ''}`} style={{ width: `${pct}%` }} /></div>
            )}
        </div>
    );
}

export default function PortalSidePanel({ portalId, onClose, onSaved }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('info');

    // Выдать новую подписку (только если нет активной)
    const [grantPlan, setGrantPlan] = useState('extended');
    const [grantDays, setGrantDays] = useState(30);
    const [grantAnalyses, setGrantAnalyses] = useState('');
    const [grantExtUsers, setGrantExtUsers] = useState('');
    const [grantResume, setGrantResume] = useState('');
    const [grantLoading, setGrantLoading] = useState(false);
    const [grantMsg, setGrantMsg] = useState(null);

    // Продлить активную подписку
    const [extDays, setExtDays] = useState(30);
    const [extLoading, setExtLoading] = useState(false);
    const [extMsg, setExtMsg] = useState(null);

    // Изменить активную подписку
    const [updEnd, setUpdEnd] = useState('');
    const [updAnalyses, setUpdAnalyses] = useState('');
    const [updExtUsers, setUpdExtUsers] = useState('');
    const [updResume, setUpdResume] = useState('');
    const [updLoading, setUpdLoading] = useState(false);
    const [updMsg, setUpdMsg] = useState(null);

    const load = () => {
        setLoading(true);
        api.get(`/admin/portals/${portalId}`)
            .then(r => {
                setDetail(r.data);
                const a = r.data.active_subscription;
                if (a) {
                    setUpdEnd(moment(a.end_date).format('YYYY-MM-DD'));
                    setUpdAnalyses(a.number_of_analyses ?? '');
                    setUpdExtUsers(a.number_of_extension_users ?? '');
                    setUpdResume(a.number_of_resume_updates ?? '');
                }
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (portalId) {
            setGrantMsg(null); setExtMsg(null); setUpdMsg(null);
            load();
        }
    }, [portalId]);

    // Сбрасываем на нужную вкладку при смене портала
    useEffect(() => {
        if (detail) {
            setTab(detail.active_subscription ? 'extend' : 'grant');
        }
    }, [detail?.id]);

    const handleGrant = async () => {
        setGrantMsg(null);
        setGrantLoading(true);
        const body = { plan_type: grantPlan, days: Number(grantDays) };
        if (grantAnalyses !== '') body.number_of_analyses = Number(grantAnalyses);
        if (grantExtUsers !== '') body.number_of_extension_users = Number(grantExtUsers);
        if (grantResume !== '') body.number_of_resume_updates = Number(grantResume);
        try {
            await api.post(`/admin/portals/${portalId}/subscription`, body);
            setGrantMsg({ ok: true, text: 'Подписка выдана' });
            onSaved?.();
            load();
        } catch (e) {
            setGrantMsg({ ok: false, text: e.response?.data?.detail || 'Ошибка' });
        } finally {
            setGrantLoading(false);
        }
    };

    const handleExtend = async () => {
        setExtMsg(null);
        setExtLoading(true);
        const subId = detail?.active_subscription?.id;
        const currentEnd = detail?.active_subscription?.end_date;
        if (!subId || !currentEnd) { setExtMsg({ ok: false, text: 'Нет активной подписки' }); setExtLoading(false); return; }
        const newEnd = moment(currentEnd).add(Number(extDays), 'days').toISOString();
        try {
            await api.patch(`/admin/subscription/${subId}`, { end_date: newEnd });
            setExtMsg({ ok: true, text: `Продлено на ${extDays} дн. Новая дата: ${moment(newEnd).format('DD.MM.YYYY')}` });
            onSaved?.();
            load();
        } catch (e) {
            setExtMsg({ ok: false, text: e.response?.data?.detail || 'Ошибка' });
        } finally {
            setExtLoading(false);
        }
    };

    const handleUpdate = async () => {
        setUpdMsg(null);
        setUpdLoading(true);
        const subId = detail?.active_subscription?.id;
        if (!subId) { setUpdMsg({ ok: false, text: 'Нет активной подписки' }); setUpdLoading(false); return; }
        const body = {};
        if (updEnd) body.end_date = new Date(updEnd).toISOString();
        if (updAnalyses !== '') body.number_of_analyses = Number(updAnalyses);
        if (updExtUsers !== '') body.number_of_extension_users = Number(updExtUsers);
        if (updResume !== '') body.number_of_resume_updates = Number(updResume);
        try {
            await api.patch(`/admin/subscription/${subId}`, body);
            setUpdMsg({ ok: true, text: 'Сохранено' });
            onSaved?.();
            load();
        } catch (e) {
            setUpdMsg({ ok: false, text: e.response?.data?.detail || 'Ошибка' });
        } finally {
            setUpdLoading(false);
        }
    };

    const hasActive = Boolean(detail?.active_subscription);
    const tabs = detail
        ? (hasActive
            ? ['info', 'extend', 'update']
            : ['info', 'grant'])
        : ['info'];
    const TAB_LABELS = { info: 'Инфо', grant: 'Выдать', extend: 'Продлить', update: 'Изменить' };

    return (
        <div className={s.panel}>
            <div className={s.panelHead}>
                <div className={s.panelHeadTop}>
                    <button className={s.closeBtn} onClick={onClose}>←</button>
                </div>
                <h2 className={s.domainTitle}>{detail?.domain ?? '...'}</h2>
            </div>

            {loading ? (
                <div className={s.loader}><div className={s.spinner} /></div>
            ) : detail ? (
                <>
                    <div className={s.meta}>
                        {detail.active_subscription
                            ? <PlanBadge plan={detail.active_subscription.plan_type} />
                            : <span className={`${s.badge} ${s.bFree}`}>Нет подписки</span>}
                        {detail.inn && <span className={s.chip}>ИНН {detail.inn}</span>}
                        <span className={`${s.chip} ${detail.is_installed ? s.chipGreen : s.chipGray}`}>
                            {detail.is_installed ? 'Установлено' : 'Не установлено'}
                        </span>
                    </div>

                    <div className={s.tabs}>
                        {tabs.map(t => (
                            <button key={t} className={`${s.tab} ${tab === t ? s.tabActive : ''}`} onClick={() => setTab(t)}>
                                {TAB_LABELS[t]}
                            </button>
                        ))}
                    </div>

                    {tab === 'info' && (
                        <div className={s.body}>
                            <div className={s.section}>
                                <div className={s.sLabel}>Использование</div>
                                <UsageBar used={detail.usage.analysis_calls} available={detail.active_subscription?.number_of_analyses} label="AI-анализов" />
                                <UsageBar used={detail.usage.update_resume_calls} available={detail.active_subscription?.number_of_resume_updates} label="Обновлений резюме" />
                                <UsageBar used={detail.usage.extension_tokens} available={detail.active_subscription?.number_of_extension_users} label="Токенов расширения" />
                            </div>

                            {detail.active_subscription && (
                                <div className={s.section}>
                                    <div className={s.sLabel}>Активная подписка</div>
                                    <div className={s.kv}>
                                        <span>Тариф</span><span><PlanBadge plan={detail.active_subscription.plan_type} /></span>
                                        <span>Начало</span><span>{moment(detail.active_subscription.start_date).format('DD.MM.YYYY')}</span>
                                        <span>Окончание</span><span>{moment(detail.active_subscription.end_date).format('DD.MM.YYYY')}</span>
                                        <span>AI-анализов</span><span>{detail.active_subscription.number_of_analyses ?? '∞'}</span>
                                        <span>Польз. расширения</span><span>{detail.active_subscription.number_of_extension_users ?? '∞'}</span>
                                        <span>Обн. резюме</span><span>{detail.active_subscription.number_of_resume_updates ?? '∞'}</span>
                                    </div>
                                </div>
                            )}

                            {detail.subscriptions.length > 0 && (
                                <div className={s.section}>
                                    <div className={s.sLabel}>История подписок</div>
                                    {detail.subscriptions.map(sub => (
                                        <div key={sub.id} className={s.histRow}>
                                            <PlanBadge plan={sub.plan_type} />
                                            <span className={s.histDates}>{moment(sub.start_date).format('DD.MM.YY')} – {moment(sub.end_date).format('DD.MM.YY')}</span>
                                            <span className={sub.is_active ? s.active : s.inactive}>{sub.is_active ? 'активна' : 'истекла'}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'extend' && (
                        <div className={s.body}>
                            <div className={s.extendInfo}>
                                <span className={s.extendLabel}>Текущее окончание</span>
                                <span className={s.extendDate}>
                                    {moment(detail.active_subscription.end_date).format('DD.MM.YYYY')}
                                </span>
                            </div>
                            <div className={s.formFields}>
                                <Field label="Продлить на (дней)">
                                    <input type="number" min={1} value={extDays} onChange={e => setExtDays(e.target.value)} />
                                </Field>
                            </div>
                            {extDays > 0 && (
                                <div className={s.extendPreview}>
                                    Новая дата: <strong>{moment(detail.active_subscription.end_date).add(Number(extDays), 'days').format('DD.MM.YYYY')}</strong>
                                </div>
                            )}
                            {extMsg && <Msg {...extMsg} />}
                            <Btn onClick={handleExtend} loading={extLoading}>Продлить подписку</Btn>
                        </div>
                    )}

                    {tab === 'grant' && (
                        <div className={s.body}>
                            <div className={s.formFields}>
                                <Field label="Тариф">
                                    <select value={grantPlan} onChange={e => setGrantPlan(e.target.value)}>
                                        {PLAN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </Field>
                                <Field label="Срок (дней)">
                                    <input type="number" min={1} value={grantDays} onChange={e => setGrantDays(e.target.value)} />
                                </Field>
                                <Field label="AI-анализов" hint={planHint(grantPlan, 'analyses')}>
                                    <input type="number" min={0} value={grantAnalyses} onChange={e => setGrantAnalyses(e.target.value)}
                                        placeholder={`По умолчанию: ${planHint(grantPlan, 'analyses') ?? 'из тарифа'}`} />
                                </Field>
                                <Field label="Польз. расширения" hint={planHint(grantPlan, 'extUsers')}>
                                    <input type="number" min={0} value={grantExtUsers} onChange={e => setGrantExtUsers(e.target.value)}
                                        placeholder={`По умолчанию: ${planHint(grantPlan, 'extUsers') ?? 'из тарифа'}`} />
                                </Field>
                                <Field label="Обновлений резюме" hint={planHint(grantPlan, 'resumeUpdates')}>
                                    <input type="number" min={0} value={grantResume} onChange={e => setGrantResume(e.target.value)}
                                        placeholder={`По умолчанию: ${planHint(grantPlan, 'resumeUpdates') ?? 'из тарифа'}`} />
                                </Field>
                            </div>
                            {grantMsg && <Msg {...grantMsg} />}
                            <Btn onClick={handleGrant} loading={grantLoading}>Выдать подписку</Btn>
                        </div>
                    )}

                    {tab === 'update' && (
                        <div className={s.body}>
                            <div className={s.formFields}>
                                <Field label="Дата окончания">
                                    <input type="date" value={updEnd} onChange={e => setUpdEnd(e.target.value)} />
                                </Field>
                                <Field label="AI-анализов">
                                    <input type="number" min={0} value={updAnalyses} onChange={e => setUpdAnalyses(e.target.value)} />
                                </Field>
                                <Field label="Польз. расширения">
                                    <input type="number" min={0} value={updExtUsers} onChange={e => setUpdExtUsers(e.target.value)} />
                                </Field>
                                <Field label="Обновлений резюме">
                                    <input type="number" min={0} value={updResume} onChange={e => setUpdResume(e.target.value)} />
                                </Field>
                            </div>
                            {updMsg && <Msg {...updMsg} />}
                            <Btn onClick={handleUpdate} loading={updLoading}>Сохранить</Btn>
                        </div>
                    )}
                </>
            ) : (
                <div className={s.loader}>Портал не найден</div>
            )}
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div className={s.field}>
            <label>{label}</label>
            {children}
            {hint && <span className={s.fieldHint}>По умолчанию: {hint}</span>}
        </div>
    );
}

function Msg({ ok, text }) {
    return <div className={ok ? s.msgOk : s.msgErr}>{text}</div>;
}

function Btn({ onClick, loading, children }) {
    return (
        <button className={s.btn} onClick={onClick} disabled={loading}>
            {loading ? <span className={s.spinner} /> : children}
        </button>
    );
}
